import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { AppState } from '@state';
import { parseError } from '@state/errors.parser';
import * as userAuthSelectors from '@state/user-auth/user-auth.selectors';
import * as managerActions from './manager.actions';
import { ManagerHttpService } from '@core/http/manager.http.service';

@Injectable()
export class ManagerEffects {
  private readonly actions$ = inject(Actions);
  private readonly managerHttp = inject(ManagerHttpService);
  private readonly snackService = inject(StackedMessagesService);
  private readonly store = inject<Store<AppState>>(Store);

  private readonly myself$ = this.store.select(userAuthSelectors.selectMyself);

  getMyManagedCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.getMyManagedCompanies),
      withLatestFrom(this.myself$),
      // Skip fetching when there is no authenticated user (e.g. logout resets the store to null and re-triggers this).
      filter(([, myself]) => !!myself?.role?.key),
      switchMap(() => {
        return this.managerHttp.getMyManagedCompanies().pipe(
          map(managedCompanies => managerActions.getMyManagedCompaniesSuccess({ managedCompanies })),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.getMyManagedCompanies.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_FETCHING_LIST_OF_MANAGED_COMPANIES' });
            return of(managerActions.getMyManagedCompaniesError({ errorMessage }));
          })
        );
      })
    )
  );

  searchProcessesOfManagedEmployees$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.searchProcessesOfManagedEmployees),
      switchMap(({ opts }) => {
        const { all, count, ...payload } = opts;
        return this.managerHttp.searchProcessesOfManagedEmployees(payload).pipe(
          map(processes => managerActions.searchProcessesOfManagedEmployeesSuccess({ processes })),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.searchProcessesOfManagedEmployees.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_SEARCHING_PROCESSES' });
            return of(managerActions.searchProcessesOfManagedEmployeesError({ errorMessage }));
          })
        );
      })
    )
  );

  searchCandidatesVerifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.searchCandidatesVerifications),
      switchMap(({ opts }) => {
        const { all, count, ...payload } = opts;
        return this.managerHttp.searchCandidatesVerifications(payload).pipe(
          map(candidateVerification => managerActions.searchCandidatesVerificationsSuccess({ candidateVerification })),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.searchCandidatesVerifications.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_SEARCHING_CANDIDATE_VERIFICATIONS' });
            return of(managerActions.searchCandidatesVerificationsError({ errorMessage }));
          })
        );
      })
    )
  );

  getCandidateVerificationDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.getCandidateVerificationDetails),
      switchMap(({ candidateVerificationId }) => {
        return this.managerHttp.getCandidateVerificationDetails(candidateVerificationId).pipe(
          map(candidateVerification =>
            managerActions.getCandidateVerificationDetailsSuccess({ candidateVerification })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.getCandidateVerificationDetails.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_FETCHING_CANDIDATE_VERIFICATION_DETAILS' });
            return of(managerActions.getCandidateVerificationDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  getCandidateVerificationHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.getCandidateVerificationHistory),
      switchMap(({ candidateVerificationId }) => {
        return this.managerHttp.getCandidateVerificationHistory(candidateVerificationId).pipe(
          map(candidateVerificationHistory =>
            managerActions.getCandidateVerificationHistorySuccess({ candidateVerificationHistory })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.getCandidateVerificationHistory.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_FETCHING_CANDIDATE_VERIFICATION_HISTORY' });
            return of(managerActions.getCandidateVerificationHistoryError({ errorMessage }));
          })
        );
      })
    )
  );

  addNewCandidateVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.addNewCandidateVerification),
      switchMap(({ opts }) => {
        return this.managerHttp.newCandidateVerification(opts).pipe(
          map(({ candidateVerification, smsResult }) =>
            managerActions.addNewCandidateVerificationSuccess({ candidateVerification, smsResult })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.addNewCandidateVerification.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_ADDING_CANDIDATE_VERIFICATION' });
            return of(managerActions.addNewCandidateVerificationError({ errorMessage }));
          })
        );
      })
    )
  );

  updateCandidateVerificationBasicDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.updateCandidateVerificationBasicDetails),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.managerHttp.updateCandidateVerificationDetails(candidateVerificationId, opts).pipe(
          switchMap(({ smsResult }) =>
            this.managerHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  managerActions.updateCandidateVerificationBasicDetailsSuccess({ candidateVerification, smsResult })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.updateCandidateVerificationBasicDetails.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_UPDATING_CANDIDATE_VERIFICATION' });
            return of(managerActions.updateCandidateVerificationBasicDetailsError({ errorMessage }));
          })
        );
      })
    )
  );

  regenerateFormLinkAndPin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.regenerateCandidateFormLinkAndPin),
      switchMap(({ candidateVerificationId }) => {
        return this.managerHttp.regenerateFormLinkAndPin(candidateVerificationId).pipe(
          switchMap(({ smsResult }) =>
            this.managerHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  managerActions.regenerateCandidateFormLinkAndPinSuccess({ candidateVerification, smsResult })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.regenerateCandidateFormLinkAndPin.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_REGENERATING_FORM_LINK_AND_PIN' });
            return of(managerActions.regenerateCandidateFormLinkAndPinError({ errorMessage }));
          })
        );
      })
    )
  );

  setCandidateVerificationDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.setCandidateVerificationDecision),
      switchMap(({ candidateVerificationId, opts }) => {
        return this.managerHttp.setCandidateVerificationDecision(candidateVerificationId, opts).pipe(
          switchMap(() =>
            this.managerHttp
              .getCandidateVerificationDetails(candidateVerificationId)
              .pipe(
                map(candidateVerification =>
                  managerActions.setCandidateVerificationDecisionSuccess({ candidateVerification })
                )
              )
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.setCandidateVerificationDecision.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_SETTING_CANDIDATE_VERIFICATION_DECISION' });
            return of(
              managerActions.setCandidateVerificationDecisionError({
                candidateVerificationId,
                opts,
                errorMessage,
              })
            );
          })
        );
      })
    )
  );

  updateCandidateVerificationProcedureDecision$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.updateCandidateVerificationProcedureDecision),
      switchMap(({ candidateVerificationId, procedureId, decision }) => {
        return this.managerHttp
          .updateCandidateVerificationProcedureDecision(candidateVerificationId, procedureId, decision)
          .pipe(
            map(() =>
              managerActions.updateCandidateVerificationProcedureDecisionSuccess({
                candidateVerificationId,
                procedureId,
                decision,
              })
            ),
            catchError(error => {
              const errorMessage = parseError(error, managerActions.updateCandidateVerificationProcedureDecision.type);
              this.snackService.showError({
                message: 'MANAGER.ERROR_UPDATING_CANDIDATE_VERIFICATION_PROCEDURE_DECISION',
              });
              return of(
                managerActions.updateCandidateVerificationProcedureDecisionError({
                  candidateVerificationId,
                  procedureId,
                  decision,
                  errorMessage,
                })
              );
            })
          );
      })
    )
  );

  addCandidateOptionalServices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(managerActions.addCandidateOptionalServices),
      switchMap(({ candidateVerificationId, services }) => {
        return this.managerHttp.addCandidateOptionalServices(candidateVerificationId, services).pipe(
          map(addedServices =>
            managerActions.addCandidateOptionalServicesSuccess({
              candidateVerificationId,
              requestedServices: services,
              addedServices,
            })
          ),
          catchError(error => {
            const errorMessage = parseError(error, managerActions.addCandidateOptionalServices.type);
            this.snackService.showError({ message: 'MANAGER.ERROR_ADDING_CANDIDATE_OPTIONAL_SERVICES' });
            return of(
              managerActions.addCandidateOptionalServicesError({
                candidateVerificationId,
                requestedServices: services,
                errorMessage,
              })
            );
          })
        );
      })
    )
  );
}
